import React, { useEffect, useMemo, useRef } from 'react';
import { getMeetingDisplayName } from '../../utils/meetingDisplayName';

const toId = (value) => String(value?._id || value?.id || value || '');

const VideoTile = ({ stream, name, local = false }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    ref.current.srcObject = stream || null;
    if (stream) ref.current.play?.().catch(() => undefined);
    return () => { if (ref.current) ref.current.srcObject = null; };
  }, [stream]);
  const hasVideo = Boolean(stream?.getVideoTracks?.().some(
    (track) => track.readyState === 'live' && track.enabled !== false
  ));
  return <div className="relative aspect-video min-w-0 overflow-hidden rounded-lg border border-white/60 bg-gradient-to-br from-indigo-950 to-slate-900 shadow-xl">
    {hasVideo ? <video
      ref={ref}
      autoPlay
      playsInline
      muted
      data-recording-participant="true"
      className={`h-full w-full object-cover ${local ? '-scale-x-100' : ''}`}
    /> : <div className="flex h-full w-full items-center justify-center text-2xl font-bold text-white">
      {String(name || '?').charAt(0).toUpperCase()}
    </div>}
    <div className="absolute inset-x-0 bottom-0 truncate bg-black/65 px-2 py-1 text-[10px] text-white">
      {name || 'Participant'}{local ? ' (You)' : ''}
    </div>
  </div>;
};

const BlackboardParticipantDock = ({ localStream, remoteStreams = {}, currentRoom, currentSessionId }) => {
  const tiles = useMemo(() => {
    const participants = (currentRoom?.participants || []).filter((item) =>
      ['admitted', 'presenting'].includes(item.status) && item.disconnected !== true
    );
    return participants.map((participant) => {
      const sessionId = String(participant.sessionId || '');
      const isLocal = sessionId === String(currentSessionId || '');
      const remote = remoteStreams[sessionId];
      const stream = isLocal
        ? localStream
        : remote?.stream || (remote?.video ? new MediaStream([remote.video]) : null);
      return {
        key: sessionId || toId(participant.user),
        stream,
        local: isLocal,
        name: getMeetingDisplayName(participant),
      };
    });
  }, [currentRoom?.participants, currentSessionId, localStream, remoteStreams]);

  if (!tiles.length) return null;
  return <div className="max-h-[32vh] overflow-y-auto rounded-xl border border-white/20 bg-slate-950/75 p-2 backdrop-blur-md">
    <div className="grid grid-cols-2 gap-2 xl:grid-cols-3">
      {tiles.map((tile) => <VideoTile key={tile.key} {...tile} />)}
    </div>
  </div>;
};

export default BlackboardParticipantDock;
